import React from "react"
import { useMantineTheme, Modal } from "@mantine/core"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { notifications } from "@mantine/notifications"
import { useFormContext } from "react-hook-form"

import classes from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/EditQuestionModal/EditQuestionModal.module.css"
import { Icon } from "@/components"
import { Button } from "@quorumus/plum"
import { QuestionModal } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/QuestionModal"
import {
    OPTION_TYPES,
    validateQuestionForm,
} from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/CreateQuestionModal.helper"
import { useForm } from "@/pages/hooks/useForm"
import { archiveCustomQuestion, updateCustomQuestion } from "@/services/customField"
import { QuestionForm } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/CreateQuestionModal"
import {
    getUpdatedValuesToSubmit,
    getDefaultValues,
} from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/EditQuestionModal/EditQuestionModal.helper"
import { useDisclosure } from "@mantine/hooks"
import { QuestionCreateUpdateForm } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/QuestionCreateUpdateForm/QuestionCreateUpdateForm"
import { CustomField } from "@/types/djangio"
import { PERSONALIZED_PAGES_KEY } from "@/pages/campaign-form/components/campaigns/personalizedMessages/Messaging"

interface Props {
    id: number

    opened: boolean
    onClose: () => void
    onSuccessCallback: (customField: CustomField) => void
}

export const EditQuestionModal = (props: Props) => {
    const queryClient = useQueryClient()

    const formContext = useFormContext()
    const personalizedPages = formContext.getValues(PERSONALIZED_PAGES_KEY) as Record<string, CustomField[]>
    const data = Object.values(personalizedPages)[0]?.find(
        (question) => question.id === props.id,
    ) as unknown as QuorumModel["CustomTag"]

    const theme = useMantineTheme()
    const form = useForm<QuestionForm>(getDefaultValues(data), validateQuestionForm, true)

    const [openedDeleteModal, deleteModalActions] = useDisclosure(false)

    const validate = () => {
        const errors = validateQuestionForm(form.values)
        const { tag_type, options_new } = form.values

        if (OPTION_TYPES.includes(tag_type) && options_new.length === 0) {
            errors.tag_type = "At least one option is required"
        }

        form.setFormErrors(errors)
        return Object.keys(errors).length === 0
    }

    const showErrorNotification = (error: string) => {
        notifications.show({
            title: "",
            message: error,
            autoClose: false,
            icon: <Icon icon="exclamation-triangle" color={theme.colors.red[5]} />,
            closeButtonProps: { "aria-label": "Dismiss notification" },
            color: "red.0",
        })
    }

    const submitMutation = useMutation({
        mutationFn: (data: QuestionForm) => updateCustomQuestion(props.id, getUpdatedValuesToSubmit(data)),
        onSuccess: (customField) => {
            form.reset()
            queryClient.invalidateQueries({ queryKey: ["custom-question", props.id] })
            queryClient.invalidateQueries({ queryKey: ["savedQuestions"] })
            props.onSuccessCallback(customField)
            props.onClose()
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (e: any) => {
            const error = e?.response?.data?.error_message ?? "Something wrong happened when editing question"
            showErrorNotification(error)
        },
    })

    const archiveMutation = useMutation({
        mutationFn: () => archiveCustomQuestion(props.id),
        onSuccess: (customField: CustomField) => {
            queryClient.invalidateQueries({ queryKey: ["custom-question", props.id] })
            queryClient.invalidateQueries({ queryKey: ["savedQuestions"] })
            props.onSuccessCallback(customField)
            props.onClose()
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (e: any) => {
            const error = e?.response?.data?.error_message ?? "Something went wrong when archiving the question"
            showErrorNotification(error)
        },
    })

    const handleSubmit = () => {
        if (!validate()) return
        submitMutation.mutate(form.values)
    }

    return (
        <QuestionModal
            title="Edit custom question"
            opened={props.opened}
            onClose={props.onClose}
            footer={
                <div className={classes.footer}>
                    <Button
                        variant="tertiary"
                        danger={true}
                        disabled={submitMutation.isLoading}
                        onClick={deleteModalActions.open}
                    >
                        Delete
                    </Button>
                    <div className={classes.rightContainer}>
                        <Button variant="secondary" disabled={submitMutation.isLoading} onClick={props.onClose}>
                            Cancel
                        </Button>
                        <Button
                            loading={submitMutation.isLoading}
                            disabled={submitMutation.isLoading}
                            onClick={handleSubmit}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            }
        >
            <QuestionCreateUpdateForm form={form} isEditing />

            <Modal
                opened={openedDeleteModal}
                onClose={deleteModalActions.close}
                centered
                withCloseButton={false}
                size="sm"
            >
                <div className={classes.deleteModal}>
                    <div className={classes.deleteIcon}>
                        <Icon icon="trash" />
                    </div>
                    <div>
                        <div className={classes.deleteTitle}>Delete custom question</div>
                        This action cannot be undone
                    </div>
                    <div className={classes.deleteButtons}>
                        <Button variant="secondary" onClick={deleteModalActions.close}>
                            Cancel
                        </Button>
                        <Button variant="accent" danger={true} onClick={() => archiveMutation.mutate()}>
                            Delete
                        </Button>
                    </div>
                </div>
            </Modal>
        </QuestionModal>
    )
}
